import { App as AntApp, Button, Card, DatePicker, Descriptions, Dropdown, Empty, Form, Input, Modal, Popconfirm, Skeleton, Space, Table, Tabs, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, CloseCircleOutlined, DownOutlined, FilePdfOutlined, PlusOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  useContract,
  useContractAmendments,
  useContractSchedule,
  useCreateAmendment,
  useCancelContract,
  useRegenerateContractPdf,
  type ContractAmendment,
  type ContractStatus,
  type ScheduleStatus,
} from '../api/contracts';
import { presignedDownload } from '@/features/customers/api/customers';
import { RoleGate, ADMIN_ROLES, MANAGER_ROLES, AGENT_ROLES } from '@/shared/auth/role';
import { useDocumentTemplates } from '@/features/document-templates/api/document-templates';
import { usePaymentsList, type Payment, type PaymentStatus } from '@/features/payments/api/payments';
import { formatUzs } from '@/shared/lib/format';
import { asApiError } from '@/shared/lib/api';

const STATUS_COLOR: Record<ContractStatus, string> = {
  draft: 'default',
  active: 'green',
  completed: 'blue',
  cancelled: 'red',
  defaulted: 'volcano',
};

const SCHEDULE_COLOR: Record<ScheduleStatus, string> = {
  pending: 'default',
  partial: 'gold',
  paid: 'green',
  overdue: 'red',
  waived: 'blue',
};

export function ContractDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = AntApp.useApp();
  const { data: contract, isLoading } = useContract(id);
  const { data: schedule } = useContractSchedule(id);
  const { data: templates } = useDocumentTemplates();
  const regen = useRegenerateContractPdf();
  const cancel = useCancelContract();
  const [cancelReason, setCancelReason] = useState('');

  if (isLoading) return <Skeleton active />;
  if (!contract) return <Empty />;

  const activeContractTemplates = (templates ?? []).filter(
    (d) => d.type === 'contract' && d.active,
  );

  /**
   * Generate a fresh PDF using the chosen template (or auto-resolved if
   * templateId is undefined) and open it in a new tab.
   */
  async function generateAndOpen(templateId?: string): Promise<void> {
    if (!contract) return;
    try {
      const { key } = await regen.mutateAsync({ id: contract.id, templateId });
      const url = await presignedDownload(key);
      void message.success(t('contracts.pdfGenerated'));
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      void message.error(asApiError(err).message);
    }
  }

  return (
    <Space direction="vertical" size={20} style={{ width: '100%' }}>
      <div>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/contracts')}>
          {t('contracts.title')}
        </Button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 8 }}>
          <Space size={12} align="baseline">
            <Typography.Title level={2} style={{ margin: 0 }}>
              {contract.contractNumber}
            </Typography.Title>
            <Tag color={STATUS_COLOR[contract.status]}>{t(`contracts.status.${contract.status}`)}</Tag>
          </Space>
          <Space>
            <RoleGate roles={ADMIN_ROLES}>
            {(contract.status === 'draft' || contract.status === 'active' || contract.status === 'defaulted') && (
              <Popconfirm
                title={t('contracts.cancelConfirmTitle')}
                description={
                  <Input
                    placeholder={t('contracts.cancelReasonPlaceholder')}
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    style={{ width: 260, marginTop: 8 }}
                  />
                }
                okText={t('contracts.cancelConfirm')}
                okButtonProps={{ danger: true, loading: cancel.isPending }}
                cancelText={t('common.cancel')}
                onConfirm={async () => {
                  try {
                    await cancel.mutateAsync({ id: contract.id, reason: cancelReason || t('contracts.cancelDefaultReason') });
                    void message.success(t('contracts.cancelSuccess'));
                    setCancelReason('');
                  } catch (err) {
                    void message.error(asApiError(err).message);
                  }
                }}
              >
                <Button danger icon={<CloseCircleOutlined />}>{t('contracts.cancel')}</Button>
              </Popconfirm>
            )}
            </RoleGate>
            <RoleGate roles={AGENT_ROLES}>
            {activeContractTemplates.length === 0 ? (
              <Button
                icon={<FilePdfOutlined />}
                onClick={() => navigate('/document-templates')}
              >
                {t('contracts.noTemplates')}
              </Button>
            ) : activeContractTemplates.length === 1 ? (
              <Button
                type="primary"
                icon={<FilePdfOutlined />}
                onClick={() => generateAndOpen(activeContractTemplates[0]!.id)}
                loading={regen.isPending}
              >
                {t('contracts.pdfAction')}
              </Button>
            ) : (
              <Dropdown
                trigger={['click']}
                menu={{
                  items: activeContractTemplates.map((d) => ({
                    key: d.id,
                    label: `${d.code} · ${d.language.toUpperCase()} · v${d.version}`,
                    onClick: () => void generateAndOpen(d.id),
                  })),
                }}
              >
                <Button type="primary" icon={<FilePdfOutlined />} loading={regen.isPending}>
                  {t('contracts.pdfAction')} <DownOutlined />
                </Button>
              </Dropdown>
            )}
            </RoleGate>
          </Space>
        </div>
      </div>

      <Tabs
        items={[
          {
            key: 'overview',
            label: t('contracts.tabs.overview'),
            children: (
              <Card>
                <Descriptions column={{ xs: 1, sm: 2 }} colon={false} labelStyle={{ color: '#64748b' }}>
                  <Descriptions.Item label={t('contracts.customer')}>
                    {contract.customer ? (
                      <Link to={`/customers/${contract.customer.id}`}>
                        {contract.customer.lastName} {contract.customer.firstName}{' '}
                        <Typography.Text type="secondary">· {contract.customer.phone}</Typography.Text>
                      </Link>
                    ) : (
                      '—'
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('contracts.apartment')}>
                    {contract.apartment?.fullCode ?? '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('contracts.totalAmount')}>{formatUzs(contract.totalAmount)}</Descriptions.Item>
                  {BigInt(contract.discountAmount ?? '0') > 0n && (
                    <Descriptions.Item label={t('contracts.discount')}>
                      <span style={{ color: '#16a34a' }}>−{formatUzs(contract.discountAmount)}</span>
                      {contract.discountReason && (
                        <span style={{ color: '#64748b', marginLeft: 8, fontSize: 12 }}>({contract.discountReason})</span>
                      )}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label={t('contracts.downPayment')}>{formatUzs(contract.downPayment)}</Descriptions.Item>
                  <Descriptions.Item label={t('contracts.installmentCount')}>
                    {contract.installmentCount} ({t(`contracts.frequency${contract.frequency === 'monthly' ? 'Monthly' : 'Quarterly'}`)})
                  </Descriptions.Item>
                  <Descriptions.Item label={t('contracts.firstDueDate')}>
                    {dayjs(contract.firstDueDate).format('DD.MM.YYYY')}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('contracts.lateFeePercentMonthly')}>
                    {contract.lateFeePercentMonthly}% / mo
                  </Descriptions.Item>
                  <Descriptions.Item label={t('contracts.gracePeriodDays')}>
                    {contract.gracePeriodDays} d
                  </Descriptions.Item>
                  <Descriptions.Item label={t('contracts.activatedAt')}>
                    {contract.activatedAt ? dayjs(contract.activatedAt).format('DD.MM.YYYY HH:mm') : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('contracts.signedDate')}>
                    {contract.signedDate ? dayjs(contract.signedDate).format('DD.MM.YYYY') : '—'}
                  </Descriptions.Item>
                  {contract.notes && (
                    <Descriptions.Item label={t('contracts.notes')} span={2}>
                      {contract.notes}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            ),
          },
          {
            key: 'schedule',
            label: t('contracts.tabs.schedule'),
            children: (
              <Card bodyStyle={{ padding: 0 }}>
                <Table
                  rowKey="id"
                  dataSource={schedule ?? []}
                  pagination={false}
                  size="small"
                  columns={[
                    { title: '#', dataIndex: 'installmentNumber', width: 60 },
                    {
                      title: t('contracts.type'),
                      dataIndex: 'isDownPayment',
                      width: 100,
                      render: (v: boolean) => (v ? t('contracts.downPaymentRow') : t('contracts.installment')),
                    },
                    {
                      title: t('contracts.dueDate'),
                      dataIndex: 'dueDate',
                      width: 130,
                      render: (v: string) => dayjs(v).format('DD.MM.YYYY'),
                    },
                    {
                      title: t('contracts.amountDue'),
                      dataIndex: 'amountDue',
                      render: (v: string) => formatUzs(v),
                    },
                    {
                      title: t('contracts.amountPaid'),
                      dataIndex: 'amountPaid',
                      render: (v: string) => formatUzs(v),
                    },
                    {
                      title: t('contracts.cols.status'),
                      dataIndex: 'status',
                      width: 110,
                      render: (s: ScheduleStatus) => (
                        <Tag color={SCHEDULE_COLOR[s]}>{t(`contracts.scheduleStatus.${s}`)}</Tag>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: 'payments',
            label: t('contracts.tabs.payments'),
            children: <PaymentsTab contractId={contract.id} />,
          },
          {
            key: 'documents',
            label: t('contracts.tabs.documents'),
            children: <DocumentsTab contractId={contract.id} contractPdfUrl={contract.contractPdfUrl} />,
          },
        ]}
      />
    </Space>
  );
}

const PAYMENT_STATUS_COLOR: Record<PaymentStatus, string> = {
  pending: 'gold',
  confirmed: 'green',
  reversed: 'red',
};

function PaymentsTab({ contractId }: { contractId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = usePaymentsList({ contractId });
  const rows = data?.data ?? [];
  if (isLoading) return <Skeleton active />;
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography.Text type="secondary">
          {t('contracts.payments.total', { count: rows.length })}
        </Typography.Text>
        <Button type="primary" onClick={() => navigate(`/payments/new?contractId=${contractId}`)}>
          {t('payments.new')}
        </Button>
      </div>
      <Card bodyStyle={{ padding: 0 }}>
        {rows.length === 0 ? (
          <Empty style={{ padding: 24 }} description={t('contracts.payments.empty')} />
        ) : (
          <Table<Payment>
            rowKey="id"
            dataSource={rows}
            pagination={false}
            size="small"
            onRow={(r) => ({ onClick: () => navigate(`/payments/${r.id}`), style: { cursor: 'pointer' } })}
            columns={[
              {
                title: t('payments.cols.date'),
                dataIndex: 'paymentDate',
                width: 120,
                render: (v: string) => dayjs(v).format('DD.MM.YYYY'),
              },
              {
                title: t('payments.cols.amount'),
                width: 180,
                render: (_, r) => formatUzs(r.amount),
              },
              {
                title: t('payments.cols.method'),
                dataIndex: 'method',
                width: 160,
                render: (m: string) => t(`apartments.paymentMethods.${m}`),
              },
              {
                title: t('payments.cols.reference'),
                dataIndex: 'reference',
                ellipsis: true,
                render: (v: string | null) => v ?? '—',
              },
              {
                title: t('payments.cols.status'),
                dataIndex: 'status',
                width: 120,
                render: (s: PaymentStatus) => (
                  <Tag color={PAYMENT_STATUS_COLOR[s]}>{t(`payments.status.${s}`)}</Tag>
                ),
              },
            ]}
          />
        )}
      </Card>
    </Space>
  );
}

function DocumentsTab({
  contractId,
  contractPdfUrl,
}: {
  contractId: string;
  contractPdfUrl: string | null;
}) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { data: amendments } = useContractAmendments(contractId);
  const createAmendment = useCreateAmendment(contractId);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  async function openKey(key: string): Promise<void> {
    try {
      const url = await presignedDownload(key);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      void message.error(asApiError(err).message);
    }
  }

  async function onOk() {
    try {
      const v = await form.validateFields();
      await createAmendment.mutateAsync({
        reason: v.reason,
        effectiveFrom: v.effectiveFrom.format('YYYY-MM-DD'),
        changes: v.description ? { description: v.description } : {},
      });
      void message.success(t('contracts.documents.amendmentCreated'));
      setOpen(false);
      form.resetFields();
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card title={t('contracts.documents.contractPdf')} bodyStyle={{ padding: 16 }}>
        {contractPdfUrl ? (
          <Space direction="vertical" size={4}>
            <Typography.Text code style={{ fontSize: 12 }}>{contractPdfUrl}</Typography.Text>
            <Button type="primary" onClick={() => void openKey(contractPdfUrl)}>
              {t('contracts.viewPdf')}
            </Button>
          </Space>
        ) : (
          <Empty
            description={t('contracts.documents.noPdfYet')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>

      <Card
        title={t('contracts.documents.amendments')}
        bodyStyle={{ padding: 0 }}
        extra={
          <RoleGate roles={MANAGER_ROLES}>
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
              {t('contracts.documents.newAmendment')}
            </Button>
          </RoleGate>
        }
      >
        {(amendments ?? []).length === 0 ? (
          <Empty style={{ padding: 24 }} description={t('contracts.documents.noAmendments')} />
        ) : (
          <Table<ContractAmendment>
            rowKey="id"
            dataSource={amendments ?? []}
            pagination={false}
            size="small"
            columns={[
              { title: t('contracts.documents.version'), dataIndex: 'version', width: 70 },
              {
                title: t('contracts.documents.effectiveFrom'),
                dataIndex: 'effectiveFrom',
                width: 130,
                render: (v: string) => dayjs(v).format('DD.MM.YYYY'),
              },
              { title: t('contracts.documents.reason'), dataIndex: 'reason', ellipsis: true },
              {
                title: t('contracts.documents.createdAt'),
                dataIndex: 'createdAt',
                width: 150,
                render: (v: string) => dayjs(v).format('DD.MM.YYYY HH:mm'),
              },
              {
                title: '',
                width: 100,
                render: (_: unknown, r: ContractAmendment) =>
                  r.amendmentPdfUrl ? (
                    <Button size="small" onClick={() => void openKey(r.amendmentPdfUrl!)}>
                      {t('contracts.viewPdf')}
                    </Button>
                  ) : null,
              },
            ]}
          />
        )}
      </Card>

      <Modal
        title={t('contracts.documents.newAmendment')}
        open={open}
        onOk={onOk}
        onCancel={() => { setOpen(false); form.resetFields(); }}
        confirmLoading={createAmendment.isPending}
        okText={t('common.create')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item name="reason" label={t('contracts.documents.reason')} rules={[{ required: true, min: 2 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="effectiveFrom" label={t('contracts.documents.effectiveFrom')} rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item name="description" label={t('contracts.documents.changesDescription')}>
            <Input.TextArea rows={3} maxLength={1000} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
